<template>
  <section class="sellers">
    <header class="sellers__header">
      <div class="sellers__header-content">
        <div class="sellers__header-title">
          <img :src="require(`@/assets/img/icons/welcome/users.svg`)" />
          <h1>{{ $tc('store-management.sellers.header-title') }}</h1>
        </div>

        <div class="sellers__header-actions">
          <transition name="fade">
            <el-dropdown v-if="activeName === 'all-sellers'" :disabled="isExporting">
              <div class="sellers__dropdown">
                <el-button size="large" round :loading="isExporting" class="sellers__dropdown-button sellers__btn">
                  <i v-if="!isExporting" class="icon icon-down-14px"></i>
                  {{ $tc('_export', 2) }}
                </el-button>
              </div>

              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item @click="exportSellers('csv')">{{ $t('export') }} .CSV</el-dropdown-item>
                  <el-dropdown-item @click="exportSellers('xls')">{{ $t('export') }} .XLS</el-dropdown-item>
                  <el-dropdown-item @click="exportSellers('xlsx')">{{ $t('export') }} .XLSX</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </transition>

          <el-button size="large" type="primary" round class="sellers__btn" @click="addSeller">{{
            $t('store-management.sellers.add-seller')
          }}</el-button>
        </div>
      </div>

      <alert
        v-if="hasSellersWarning && enabledSellersWarning"
        type="warning"
        :message="$tc('store-management.sellers.warning.alert')"
        class="sellers__alert"
      />
    </header>

    <el-tabs v-model="activeName" @tab-click="handleClick">
      <el-tab-pane :label="$t('store-management.sellers.title')" name="all-sellers">
        <sellers-table v-if="activeName === 'all-sellers'" />
      </el-tab-pane>

      <el-tab-pane :label="$t('store-management.sellers.warning.title')" name="warning-sellers">
        <warning-sellers-table v-if="activeName === 'warning-sellers'" />
      </el-tab-pane>
    </el-tabs>

    <seller-detail :seller-id="$route.params.sellerId" />
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
// libs
import { defineComponent } from 'vue';
import { useToast } from 'vue-toastification';
// helpers
import bus from '@/helpers/events/bus';
import { tableMixin, verifyMixin } from '@/helpers/mixins';
// utilities
import { useMixpanelConfig } from '@/utilities/mixpanel';
// store
import Alert from '@/components/_atoms/Alert.vue';
import SellerDetail from '@/components/oto/sellers/SellerDetail.vue';
import SellersTable from '@/features/Sellers/components/SellersTable.vue';
import { getExportSellers } from '@/services/oto/sellers';
import store from '@/store';
import { dateFormat } from '@/utilities/constants';
import WarningSellersTable from '@/views/store/WarningSellersTable.vue';
import fileDownload from 'js-file-download';
import moment from 'moment';

export default defineComponent({
  name: 'Sellers',
  components: {
    SellersTable,
    WarningSellersTable,
    Alert,
    SellerDetail,
  },
  mixins: [tableMixin, verifyMixin],
  setup() {
    const toast = useToast();

    return { toast };
  },
  data() {
    return {
      id: 'sellers-list',
      order: null,
      isActive: '',
      sellersList: [],
      hasSellersWarning: null,
      selectItems: [{ id: 'tab-all-sellers', name: this.$t('store-management.sellers.title') }],
      items: { 'tab-all-sellers': 'tab-all-sellers' },

      // new
      activeName: 'all-sellers',
      isExporting: false,
    };
  },
  computed: {
    ...mapGetters('sellers', ['getSelectedTab', 'getFormattedFilters', 'getStatusFilter', 'getEnabledValue']),
    ...mapState('sellers', ['loadingTab']),

    enabledSellersWarning() {
      return store?.getters?.getEnabledSellersWarning;
    },

    getBrand() {
      return store.state.auth.selectedBrand?.brand;
    },

    filterArgs() {
      return this.getFormattedFilters;
    },
  },

  mounted() {
    useMixpanelConfig('view_oto_sellers');

    this.setSelectedTab({
      id: 'tab-all-sellers',
      name: this.$t('store-management.sellers.title'),
    });

    bus.$on('has-sellers-warning', value => {
      this.setLoadingTab(true);
      this.hasSellersWarning = value;
      this.setSelected(this.getSelectedTab);
      this.setLoadingTab(false);
    });
  },
  beforeUnmount() {
    this.setLoadingTab(true);

    bus.$off('render-cards');
    bus.$off('sellers-reload-list');
    bus.$off(`${this.id}-change-page`);
    bus.$off('has-sellers-warning');
  },
  methods: {
    ...mapActions('sellers', ['setSelectedTab', 'setLoadingTab']),

    handleClick(tab) {
      this.activeName = tab.name;
      this.setSelected(tab);
    },

    addSeller() {
      this.$router.push({
        name: 'store-management-sellers-detail',
        params: { sellerId: 'create' },
      });
    },

    setSelected(tab) {
      const isActive = Object.keys(this.items).includes('tab-warning-sellers');

      if (this.enabledSellersWarning && !isActive) {
        this.items = { ...this.items, 'tab-warning-sellers': 'tab-warning-sellers' };

        const warningSellers = {
          id: 'tab-warning-sellers',
          name: this.$t('store-management.sellers.warning.title'),
        };

        if (this.hasSellersWarning) warningSellers.icon = 'alert';
        this.selectItems.push(warningSellers);
      }
      this.setSelectedTab(tab);
    },

    selectedTab(selected) {
      return this.getSelectedTab.id === this.items[selected];
    },

    exportSellers(fileType) {
      this.isExporting = true;

      useMixpanelConfig(`export_oto_sellers_${fileType}`);

      const date = moment().format(dateFormat);
      const filtersWithExportType = {
        export: fileType,
        ...(this.filterArgs.length && { query: JSON.stringify(this.filterArgs) }),
        enabled: this.getEnabledValue(this.getStatusFilter),
      };

      getExportSellers(fileType, 'blob', filtersWithExportType)
        .then(data => {
          const filename = `${this.$t('store-management.sellers.export-filename')} - ${this.getBrand} - ${this.$t(
            'store-management.sellers.export-suffix',
          )} ${date}.${fileType}`;

          fileDownload(data, filename);
        })
        .finally(() => {
          this.isExporting = false;
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.sellers {
  border-radius: 8px;
  background: $color-white;
  box-shadow: 0px 20px 60px 0px rgba(51, 58, 69, 0.07);
  margin-top: -30px;

  :deep(.el-tabs__header) {
    margin: 0;
  }

  &__btn {
    font-size: 12px;
    font-weight: 600;
  }

  &__header {
    padding: 18px 20px 0px;
    display: grid;
    gap: 20px;
  }

  &__header-title {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-left: 5px;

    h1 {
      font-weight: 600;
      font-size: 18px;
      line-height: 24px;
      margin: 0;
    }

    img {
      width: 20px;
    }
  }

  &__header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;

    :deep(.el-tooltip__trigger) {
      &:focus,
      &:focus-visible {
        outline: none !important;
        box-shadow: none !important;
      }
    }
  }

  &__dropdown-button {
    border-color: $oto-omni;
    color: $oto-omni;

    .icon {
      margin-right: 8px;
    }
  }
}
</style>

<style lang="scss">
.sellers {
  .el-tabs__nav-scroll {
    padding: 0 15px;
  }
}
</style>
